import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type { StrictPreloadable } from '@/services/preloaders/types'
import type PreloadRepository from '@/data/repositories/preloadRepository'

@injectable()
export default abstract class StrictPreloaderService<P = void> implements StrictPreloadable {
  protected constructor(
    @inject(SERVICE_TYPES.PreloadRepository) protected readonly preloadRepository: PreloadRepository<P>,
  ) {}

  protected key = 'strictPreloaderService'

  public async preload(): Promise<void> {
    if (this.isPreloaded()) {
      return
    }

    this.startPreloading()

    try {
      const payload = await this.loadData()
      this.markAsPreloaded(payload)
    } catch (error) {
      this.markAsFailed()
      throw error
    }
  }

  public isPreloaded(): boolean {
    const preload = this.getPreload()
    return preload?.isPreloaded || false
  }

  public getPayload(): P | undefined {
    const preload = this.getPreload()
    return preload?.payload
  }

  public startPreloading(): void {
    this.preloadRepository.insertItems([
      {
        id: this.key,
        isLoading: true,
        isFailed: false,
        isPreloaded: false,
        updatedAt: new Date().toISOString(),
      },
    ])
  }

  public markAsFailed(): void {
    this.preloadRepository.updateItem({
      id: this.key,
      isLoading: false,
      isFailed: true,
      isPreloaded: false,
      updatedAt: new Date().toISOString(),
    })
  }

  public markAsPreloaded(payload?: P): void {
    this.preloadRepository.updateItem({
      id: this.key,
      isLoading: false,
      isFailed: false,
      isPreloaded: true,
      updatedAt: new Date().toISOString(),
      payload,
    })
  }

  public markAsNotPreloaded(): void {
    this.preloadRepository.updateItem({
      id: this.key,
      isLoading: false,
      isFailed: false,
      isPreloaded: false,
      updatedAt: new Date().toISOString(),
    })
  }

  public abstract loadData(): Promise<P>

  private getPreload() {
    return this.preloadRepository.findEntityByIdOrNull(this.key)
  }
}
